<template>
  <TasksCard
    v-model="showDownloadZipSnackbar"
    v-model:expanded="expanded"
    :btn-close-props="{
      disabled: isAnyDownloadZipSnackbarInProgress,
    }"
    :title="
      isAnyDownloadZipSnackbarInProgress
        ? t(
            'node.download.zip_snackbar.generating_zip_file',
            downloadZipItems.size,
          )
        : t('node.download.zip_snackbar.zip_download')
    "
    @click:close="onClose"
    @click:expanded="expanded = !expanded"
    @mouseleave="onMouseLeave"
    @mouseover="onMouseOver"
  >
    <template v-if="isAnyDownloadZipSnackbarInProgress && !expanded" #prepend>
      <VProgressCircular
        v-tooltip="`${overallDownloadZipProgress} %`"
        class="ml-2"
        color="primary"
        :indeterminate="overallDownloadZipProgress === 0"
        :model-value="overallDownloadZipProgress"
        size="20"
        width="2"
      >
        <div class="progress-number">
          {{ overallDownloadZipProgress }}
        </div>
      </VProgressCircular>
    </template>

    <template #content>
      <v-list
        v-show="expanded"
        ref="listRef"
        class="py-0 mt-1"
        color="surface"
        max-height="200"
      >
        <v-list-item
          v-for="(item, index) in downloadZipItemsReversed"
          :key="`node-zip-item-${index}`"
          density="compact"
          slim
        >
          <template #prepend>
            <v-icon
              v-if="item.getError()"
              color="error"
              icon="far fa-circle-exclamation"
            />
            <v-icon v-else-if="item.getProgress() < 100">
              <VProgressCircular
                v-tooltip="`${item.getProgress()} %`"
                color="primary"
                :indeterminate="item.getProgress() === 0"
                :model-value="item.getProgress()"
                :rotate="360"
                :size="22"
                width="2"
              >
                <span
                  v-if="item.getProgress() > 0"
                  class="text-body-large progress-number"
                >
                  {{ item.getProgress() }}
                </span>
              </VProgressCircular>
            </v-icon>
            <v-icon v-else-if="item.getProgress() >= 100" color="success">
              far fa-check-circle
            </v-icon>
          </template>
          <template #title>
            <span>
              {{ getName(item.nodes) }}
            </span>
          </template>
          <template v-if="item.getError()" #subtitle>
            <span class="text-error text-body-small">
              {{ getDownloadZipTaskItemErrorMsg(item.getError()) }}
            </span>
          </template>
          <template v-if="item.getProgress() < 100 && !item.getError()" #append>
            <v-btn
              v-tooltip="{
                text: t('actions.cancel'),
                location: 'top',
                openDelay: 200,
              }"
              color="error"
              data-test-id="cancel-downloading"
              density="compact"
              icon="far fa-xmark"
              variant="text"
              @click="item.abortController.abort()"
            />
          </template>
        </v-list-item>
      </v-list>
    </template>

    <template #loader>
      <v-progress-linear
        v-if="closeInterval"
        absolute
        color="primary"
        location="bottom"
        :max="DEFAULT_CLOSE_TIME_LEFT_COUNTER"
        :model-value="closeTimeLeft"
      ></v-progress-linear>
    </template>
  </TasksCard>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useGoTo } from 'vuetify'
import { AXIOS_ERROR_CODES } from '@/types/axios.ts'
import { isAxiosError } from 'axios'
import { useDownload } from '@/composables/download'
import TasksCard from '@/components/tasks/TasksCard.vue'
import type { Node } from '@deepcloud/api-deep-box'
import type { ZipDownloadError } from '@deepcloud/deep-ui-kit'

export type DownloadZipTaskItem = {
  nodes: Node[]
  abortController: AbortController
  // use a getter function, because Set does not understand the type Ref in the object.
  getProgress: () => number
  getError: () => ZipDownloadError
}

const goTo = useGoTo()

const visibility = useDocumentVisibility()

const { t } = useI18n()
const expanded = ref(false)

const { showDownloadZipSnackbar, downloadZipItems } = useDownload()

const downloadZipItemsReversed = computed(
  () => new Set([...downloadZipItems.value].reverse()),
)

function getName(nodes: Node[]) {
  return nodes.reduce((previousValue, currentNode) => {
    const name = currentNode.displayName || currentNode.name

    if (previousValue) {
      return `${previousValue}, ${name}`
    }

    return name
  }, '')
}

function onClose() {
  showDownloadZipSnackbar.value = false
  downloadZipItems.value.clear()
}

function onCloseIfVisible() {
  if (visibility.value === 'visible') {
    onClose()
  }
}

const isAnyDownloadZipSnackbarInProgress = computed(() =>
  [...downloadZipItemsReversed.value].some(
    (i) => i.getProgress() < 100 && !i.getError(),
  ),
)
const hasErrors = computed(() =>
  [...downloadZipItemsReversed.value].some((i) => i.getError()),
)

function getDownloadZipTaskItemErrorMsg(e: ZipDownloadError) {
  if (isAxiosError(e)) {
    if (e.code === AXIOS_ERROR_CODES.ERR_NETWORK) {
      return t('error.axios.ERR_NETWORK')
    }
  } else {
    return t('error.error_occurred')
  }
}

const overallDownloadZipProgress = computed(() => {
  const items = [...downloadZipItemsReversed.value]
  if (items.length === 0) return 0

  const totalProgress = items.reduce((sum, item) => sum + item.getProgress(), 0)

  return Math.round(totalProgress / items.length)
})

const DEFAULT_CLOSE_TIME_LEFT_COUNTER = 100
const closeInterval = shallowRef()
const closeTimeLeft = ref(DEFAULT_CLOSE_TIME_LEFT_COUNTER)

function onClearCloseInterval() {
  closeTimeLeft.value = DEFAULT_CLOSE_TIME_LEFT_COUNTER
  clearInterval(closeInterval.value)
  closeInterval.value = undefined
}

function onStartCloseTimer() {
  closeInterval.value = setInterval(() => {
    if (closeTimeLeft.value <= 0) {
      onCloseIfVisible()
      onClearCloseInterval()
      return
    }
    closeTimeLeft.value -= 1
  }, 50)
}

function onMaybeClose() {
  if (isAnyDownloadZipSnackbarInProgress.value) return

  // only close automatically when there is no errors
  if (!hasErrors.value) {
    onStartCloseTimer()
  }
}

function onMouseLeave() {
  onMaybeClose()
}

function onMouseOver() {
  if (closeInterval.value) {
    onClearCloseInterval()
  }
}

watch(isAnyDownloadZipSnackbarInProgress, (inProgress) => {
  if (!inProgress) {
    expanded.value = true
    onMaybeClose()
  }
})

const listRef = ref()

watch(
  () => downloadZipItemsReversed.value.size,
  (size) => {
    if (size === 0) {
      onCloseIfVisible()
    } else {
      showDownloadZipSnackbar.value = true
      expanded.value = true
      goTo(0, {
        container: listRef.value,
      })
      onClearCloseInterval()
    }
  },
)

watch(visibility, (current, previous) => {
  // prevents closing right after the document becomes visible when the timeout is running
  if (current === 'visible' && previous === 'hidden') {
    onMaybeClose()
  } else if (current === 'hidden' && previous === 'visible') {
    onClearCloseInterval()
  }
})

/*
 * PLEASE READ: this is disabled because triggers after each "download"
 * then call the fn downloadFileByUrlAndName
 * */
// prevent window reload until is loading
// useEventListener(window, 'beforeunload', (evt) => {
//   if (!isAnyDownloadZipSnackbarInProgress.value) return
//   evt.preventDefault()
//   // Required for Chrome to show confirmation dialog
//   evt.returnValue = ''
// })
</script>

<style lang="scss" scoped>
.progress-number {
  font-size: 10px !important;
}
</style>
